import React from "react";
import { Card, Row, Col, Statistic, Alert, Tag, Collapse, Badge } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { LoanBreakdownData } from "../helpers/shared-interfaces";
import { tabPaneStyle } from "../helpers/constants";
import { formatCurrency, formatLabel } from "../helpers/utilities";

const { Panel } = Collapse;

interface LoanEligibilityCardProps {
  loanBreakdownData: LoanBreakdownData;
}

const AllocationSection: React.FC<{
  title: string;
  allocations: Record<string, string | number>;
}> = ({ title, allocations }) => {
  if (!allocations || Object.keys(allocations).length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ marginBottom: 12 }}>{title}</h4>
      {Object.entries(allocations).map(([key, value]) => (
        <div
          key={key}
          style={{
            padding: "12px 16px",
            marginBottom: 12,
            backgroundColor: "#fafafa",
            borderRadius: 4,
            border: "1px solid #f0f0f0",
          }}
        >
          <Row key={key} style={{ marginBottom: 4 }}>
            <Col span={12}>
              <span style={{ fontWeight: 500 }}>{formatLabel(key)}:</span>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              GHS {formatCurrency(value)}
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
};

const LienBreakdownSection: React.FC<{
  lienBreakdown: Record<string, string | number>;
}> = ({ lienBreakdown }) => {
  if (!lienBreakdown || Object.keys(lienBreakdown).length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ marginBottom: 12 }}>Lien Breakdown</h4>
      {Object.entries(lienBreakdown).map(([key, value]) => (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: 12,
            backgroundColor: "#fafafa",
            borderRadius: 4,
            border: "1px solid #f0f0f0",
          }}
        >
          <Row key={key} style={{ marginBottom: 4 }}>
            <Col span={12}>
              <span style={{ fontWeight: 500 }}>{formatLabel(key)}:</span>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
             GHS {formatCurrency(value)}
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
};

export const LoanEligibilityCard: React.FC<LoanEligibilityCardProps> = ({
  loanBreakdownData,
}) => {
  if (!loanBreakdownData?.data) {
    return null;
  }

  const { data } = loanBreakdownData;
  const isEligible = data.eligible_for_loan;
  const hasLien = parseFloat(data.total_lien_amount || "0") !== 0;

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Loan Eligibility Information</span>
          {isEligible ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Eligible
            </Tag>
          ) : (
            <Tag color="red">Not Eligible</Tag>
          )}
        </div>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="Loanable Amount"
            value={parseFloat(data.loanable_amount)}
            precision={2}
            prefix="GHS"
            valueStyle={{
              color: isEligible ? "#3f8600" : "#cf1322",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="Total Contributions"
            value={parseFloat(data.allocations.contribution_amount)}
            precision={2}
            prefix="GHS"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="Redeemable Amount"
            value={parseFloat(data.allocations.redeemable_contribution_amount)}
            precision={2}
            prefix="GHS"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="Last Contribution"
            value={parseFloat(data.last_scheme_contribution_amount)}
            precision={2}
            prefix="GHS"
          />
        </Col>
      </Row>

      {hasLien && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Total Lien Amount"
              value={parseFloat(data.total_lien_amount)}
              precision={2}
              prefix="GHS"
              valueStyle={{ color: "#d46b08" }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Price Date"
              value={data.price_date || "N/A"}
              valueStyle={{ fontSize: "16px" }}
            />
          </Col>
        </Row>
      )}

      {!isEligible && (
        <Alert
          message="Not Eligible"
          description="Customer is not eligible for this loan amount based on current contributions"
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isEligible && (
        <Collapse style={{ marginTop: 16 }}>
          <Panel
            header={
              <span>
                Loan Breakdown <Badge count={1} style={tabPaneStyle} />
              </span>
            }
            key="allocations"
          >
            {" "}
            <Row gutter={[16, 16]}>
              {data.allocations.contribution_allocations && (
                <Col xs={24} md={12}>
                  <AllocationSection
                    title="Contribution Allocations"
                    allocations={data.allocations.contribution_allocations}
                  />
                </Col>
              )}

              {data.allocations.redeemable_contribution_allocations && (
                <Col xs={24} md={12}>
                  <AllocationSection
                    title="Redeemable Contribution Allocations"
                    allocations={
                      data.allocations.redeemable_contribution_allocations
                    }
                  />
                </Col>
              )}

              {data.lien_allocations && (
                <Col xs={24} md={12}>
                  <AllocationSection
                    title="Lien Allocations"
                    allocations={data.lien_allocations}
                  />
                </Col>
              )}

              {/* Lien Breakdown */}
              {data.lien_breakdown && (
                <Col xs={24} md={12}>
                  <LienBreakdownSection lienBreakdown={data.lien_breakdown} />
                </Col>
              )}
            </Row>
          </Panel>
        </Collapse>
      )}
    </Card>
  );
};
